import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const empty = { sku: "", name: "", category: "general", price: 0, cost: 0, stock: 0, low_stock_threshold: 5, gst_rate: 18, hsn: "" };

export default function Parts() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);

  const load = () => api.get("/parts").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const payload = { ...f, price: Number(f.price), cost: Number(f.cost), stock: Number(f.stock), gst_rate: Number(f.gst_rate), low_stock_threshold: Number(f.low_stock_threshold) };
      await api.post("/parts", payload);
      toast.success("Part added");
      setF(empty); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const restock = async (pid) => {
    const qty = Number(prompt("Restock quantity:", "10"));
    if (!qty) return;
    try { await api.post(`/parts/${pid}/restock?qty=${qty}`); toast.success("Restocked"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">Warehouse</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Parts Inventory</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-part-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
              <Plus className="w-4 h-4 mr-1" /> New Part
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm bg-card border-border max-w-xl">
            <DialogHeader><DialogTitle className="font-display uppercase">New Part</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {[["sku", "SKU"], ["name", "Name"], ["category", "Category"], ["hsn", "HSN Code"], ["price", "Selling Price"], ["cost", "Cost"], ["stock", "Initial Stock"], ["low_stock_threshold", "Low Stock Alert"], ["gst_rate", "GST %"]].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{l}</Label>
                  <Input data-testid={`part-${k}`} value={f[k]} onChange={(e) => setF(x => ({ ...x, [k]: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                </div>
              ))}
            </div>
            <Button data-testid="save-part-btn" onClick={submit} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save Part</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">SKU</th><th className="text-left p-3">Name</th>
              <th className="text-left p-3">Category</th><th className="text-right p-3">Price</th>
              <th className="text-right p-3">Stock</th><th className="text-right p-3">GST</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No parts</td></tr>}
            {pg.pageItems.map(p => {
              const low = p.stock <= p.low_stock_threshold;
              return (
                <tr key={p.id} className="border-t border-border hover:bg-secondary/50">
                  <td className="p-3 font-mono text-xs text-primary uppercase">{p.sku}</td>
                  <td className="p-3">{p.name}</td>
                  <td className="p-3 text-muted-foreground text-xs uppercase tracking-wide">{p.category}</td>
                  <td className="p-3 text-right font-mono">{inr(p.price)}</td>
                  <td className="p-3 text-right">
                    <span className={`font-mono px-2 py-1 rounded-sm text-xs ${low ? "text-yellow-500 bg-yellow-500/10" : "text-green-500 bg-green-500/10"}`}>{p.stock}</span>
                  </td>
                  <td className="p-3 text-right font-mono text-xs text-muted-foreground">{p.gst_rate}%</td>
                  <td className="p-3 text-right">
                    <Button data-testid={`restock-${p.id}`} size="sm" variant="ghost" onClick={() => restock(p.id)} className="h-7 text-xs uppercase tracking-wider">
                      <PackagePlus className="w-3 h-3 mr-1"/> Restock
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
